import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { Observable, filter, map, of } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse, DynamicApiResponsePaginatedModel } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { DateTime } from 'luxon';
import { IReservationApiManageReferralDiscounts, ReservationBookingReferralGuests, IResApiManageBkgLoyaltyItemsParams, ResBkgLoyaltyRedeemItems, ResBkgLoyaltyAvailableItems } from '../models/res-booking.model';
import { IResPastGuestReferralParameters, IResReferralParameters, IReservationApiAgency, IReservationApiAgent, IReservationApiAgentParameters, IReservationApiDeleteGuestParameters, IReservationApiIndividual, IReservationApiIndividualsParameters, IReservationApiInsertGuestParameters, IReservationApiReplaceGuestParameters, IReservationApiUpdateGuestParameters, ResPastGuestReferral, ReservationAgency, ReservationAgent, ReservationIndividual } from '../models/res-owner.model';
import { IResApiGetPackageTravelDateGuide, IResApiGuestsManage, IResManagePackageTravelDateGuide, ResPackageTravelDateGuide } from '../models/res-package.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationPeopleApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  checkGuestActivity(bkgID?: number, guestCod?: string): Observable<any[]> {
    return this.getDynamicAPI('guest/checkGuestActivity', { BkgID: bkgID, GuestCod: guestCod }).pipe(
      map<DynamicApiResponse, any[]>((response) => {
        return response.body
      })
    );
  }

  checkReferralPastGuest(bkgID: number, params: { IndividualID: number }[]) {
    return this.callDynamicAPI('referral/CheckPastGuest', { BkgID: bkgID, Guests: params }, 'POST').pipe(
      map<DynamicApiResponse, ResPastGuestReferral[]>((response) => {
        return response.body.map((r: IResPastGuestReferralParameters) => new ResPastGuestReferral(r));
      })
    );
  }

  checkReferralVerification(bkgID: number) {
    return this.callDynamicAPI('booking/getReferralIndividualVerification', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, { IsVerified: string, Verified: string }>((response) => response.body[0])
    );
  }

  clearApiCache() {
    return this.getExternalAPI('cache/redis/clear', 'DELETE', {}).pipe(
      map<any, any>((response) => {
        if (response?.data?.cleared === 0) return false

        return true
      })
    )
  }

  /**
   * Delete Guest
   *
   * @param {IReservationApiDeleteGuestParameters} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public deleteGuest(params: IReservationApiDeleteGuestParameters) {
    return this.callDynamicAPI('guest/delete', params, 'POST').pipe(
      map<DynamicApiResponse, DynamicApiResponse>((response) => {
        if (response.body && response.body?.length > 0 && response.body[0].ErrorCod) {
          response.errors = response.body.map((item: any) => {
            return {
              ErrorCod: item.ErrorCod,
              ErrorDesc: item.ErrorMessage
            }
          })
        }
        return response.body;
      })
    );
  }

  getBkgLoyaltyItems(bkgID: number): Observable<{ redeemItems: ResBkgLoyaltyRedeemItems[], availableItems: ResBkgLoyaltyAvailableItems[] }> {
    return this.getDynamicAPI('booking/getBkgLoyalty', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, { redeemItems: ResBkgLoyaltyRedeemItems[], availableItems: ResBkgLoyaltyAvailableItems[] }>((response) => {
        const header = response.body.DT0 ?? []
        const redeems = response.body.DT1 ?? []
        const availables = response.body.DT2 ?? []

        const redeemItems = header.map((v: any) => {
          v.Details = redeems.filter((d: any) => d.GuestCod === v.GuestCod)
          return new ResBkgLoyaltyRedeemItems(v)
        })
        const availableItems = header.map((v: any) => {
          v.Details = availables.filter((d: any) => d.GuestCod === v.GuestCod)
          return new ResBkgLoyaltyAvailableItems(v)
        })
        return { redeemItems, availableItems }
      })
    );
  }

  /**
   * Get individual household
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public getHousehold(household_id: number) {
    return this.callDynamicAPI('individual/select', { household_id }, 'GET').pipe(
      map<DynamicApiResponse, ReservationIndividual[]>((response) => {
        return response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r));
      })
    );
  }

  getIndividualGuests(filters: IReservationApiIndividualsParameters) {
    return this.callDynamicAPI(
      'individual/select',
      filters,
      'GET'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationIndividual>>(
        (response) => {
          const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationIndividual>(response);
          paginatedModel.content = response.body.map(
            (individual: IReservationApiIndividual) => new ReservationIndividual(individual)
          );
          return paginatedModel;
        })
    );
  }

  getPackageTravelDateGuide(params?: IResApiGetPackageTravelDateGuide): Observable<ResPackageTravelDateGuide[]> {
    return this.getDynamicAPI('package/getPackageTravelDateGuide', params).pipe(
      map<DynamicApiResponse, ResPackageTravelDateGuide[]>((response) => {
        return response.body.map((data: any) => new ResPackageTravelDateGuide(data))
      })
    );
  }

  public getPaginatedAgencies(filters?: IReservationApiAgency, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('agency/select', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiAgency) => new ReservationAgency(r)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  public getPaginatedAgents(filters: IReservationApiAgentParameters, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('agent/agency', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiAgent) => new ReservationAgent(r)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  public getPaginatedIndividuals(filters?: IReservationApiIndividualsParameters, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('individual/select', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  public getPaginatedOwners(filters?: IReservationApiIndividualsParameters, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('individualOwners/select', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  getPortfolioGuests(individualID: number[], bkgID: number) {
    return this.callDynamicAPI('booking/getPortfolioGuests', { DirectID: individualID, BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, ReservationBookingReferralGuests[]>((response) => response.body.map((g: any) => new ReservationBookingReferralGuests(g))
      )
    );
  }

  getReferralIndividualID(params: IResReferralParameters) {
    return this.callDynamicAPI('booking/GetGuestReferral', params, 'GET').pipe(
      map<DynamicApiResponse, { GuestReferralID: number, OldDirectID: number }>((response) => response.body[0])
    );
  }

  guestsManage(params: IResApiGuestsManage): Observable<any[]> {
    return this.postDynamicAPI('guests/manage', params).pipe(
      map<DynamicApiResponse, any[]>((response) => {
        return response.body
      })
    );
  }

  /**
   * Create Guest
   *
   * @param {IReservationApiInsertGuestParameters} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public insertGuest(params: IReservationApiInsertGuestParameters) {
    return this.callDynamicAPI('guest/insert', params, 'POST');
  }

  mailGuestChecker(individualID: number) {
    return this.callDynamicAPI('booking/checkMailGuestReferral', { DirectID: individualID }, 'GET').pipe(
      map<DynamicApiResponse, { DirectID: number, Email: string }>((response) => response.body[0])
    );
  }

  /**
   * Manage Agencies
   *
   * @param {IReservationApiAgency} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public manageAgencies(params: IReservationApiAgency) {
    return this.callDynamicAPI('agency/manage', params, 'POST');
  }

  /**
   * Manage Agents
   *
   * @param {IReservationApiAgent} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public manageAgents(params: IReservationApiAgent) {
    return this.callDynamicAPI('agent/manage', params, 'POST');
  }

  manageBkgLoyaltyItems(params: IResApiManageBkgLoyaltyItemsParams): Observable<any> {
    return this.postDynamicAPI('booking/manageBkgLoyalty', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  /**
   * Manage Individuals
   *
   * @param {IReservationApiIndividual} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public manageIndividuals(params: IReservationApiIndividual) {
    return this.callDynamicAPI('individual/manage', params, 'POST');
  }

  managePackageTravelDateGuide(params: IResManagePackageTravelDateGuide) {
    return this.postDynamicAPI('package/managePackageTravelDateGuide', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  managePortfolioDiscounts(bkgID: number, tblPortfolio: IReservationApiManageReferralDiscounts[]) {
    return this.callDynamicAPI('booking/managePortfolioDiscountReferral', { BkgID: bkgID, TblPortfolio: tblPortfolio }, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageReferral(params: IResReferralParameters) {
    if (this.callMillisec > 0 && DateTime.now().toMillis() - this.callMillisec < 1000) return of(0)
    return this.callDynamicAPI('booking/ManageGuestReferral', params, 'POST').pipe(
      map<DynamicApiResponse, number>((response) => response.body)
    );
  }

  /**
   * Associate Directs to Guest
   *
   * @param {IReservationApiReplaceGuestParameters} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public replaceGuest(params: IReservationApiReplaceGuestParameters) {
    return this.postDynamicAPI('guest/replace', params);
  }

  /**
   * Update Guest
   *
   * @param {IReservationApiUpdateGuestParameters} params
   * @return {*}
   * @memberof ReservationPeopleApiService
   */
  public updateGuest(params: IReservationApiUpdateGuestParameters) {
    return this.callDynamicAPI('guest/update', params, 'POST')
  }
}
